import { PortfolioItem } from '@/hooks/useProfile';
import { Json } from '@/integrations/supabase/types';

/**
 * Normalizes a collection of portfolio items to ensure consistent structure
 */
export const normalizePortfolioItems = (items: any[] | null): PortfolioItem[] => {
  // Handle null/undefined input
  if (!items) return [];
  
  // Handle non-array input
  if (!Array.isArray(items)) return [];
  
  // Process each item
  return items.map(item => normalizePortfolioItem(item));
};

/**
 * Normalizes a single portfolio item to ensure consistent structure
 */
export const normalizePortfolioItem = (item: any): PortfolioItem => {
  // Handle null/undefined input
  if (!item) {
    return {
      id: crypto.randomUUID(),
      title: 'Untitled Project',
      description: '',
      image_url: '',
      images: [],
      created_at: new Date().toISOString(),
      is_featured: false,
      status: 'published',
    };
  }

  // Generate a random ID if none exists
  const id = item.id || crypto.randomUUID();
  
  // Get title with fallback
  const title = item.title || 'Untitled Project';
  
  // Handle description
  const description = item.description || '';
  
  // Handle image_url for legacy items
  const image_url = item.image_url || '';
  
  // Handle images array for new format
  let images = [];
  
  if (Array.isArray(item.images)) {
    images = item.images.map((img: any, index: number) => ({
      id: img.id || `img-${id}-${index}`,
      url: img.url || '',
      order: typeof img.order === 'number' ? img.order : index,
      caption: img.caption || '',
      // Track if this is a legacy image that was converted
      isLegacy: !!img.isLegacy
    }));
  } 
  // For legacy items with only image_url, create an images array with one entry
  else if (image_url && !Array.isArray(item.images)) {
    images = [{
      id: `legacy-${id}-0`,
      url: image_url,
      order: 0,
      caption: '',
      isLegacy: true
    }];
  }
  
  // Create the normalized item
  return {
    id,
    title,
    description,
    image_url,
    images,
    created_at: item.created_at || new Date().toISOString(),
    is_featured: item.is_featured || false,
    // Default to published for items without status
    status: item.status || 'published',
  };
};
